import {
  apiDelete, apiGet, apiPost,
  apiPut,
} from '~/_shared/utils/api/api.helpers';
import type { DataLayerServerModel } from '~/map/map.repository';
import { mapSettingsDataLayersResponseInstanceToState } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.serializer';
import type { DataLayer } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';

export type DataLayerTemplateId = number;
export type DataLayerTemplateData = Omit<DataLayer, 'id' | 'fromTemplate'>;
export type DataLayerTemplateDataResponse = Omit<DataLayerServerModel, 'id' | 'fromTemplate'>;

export type DataLayerTemplate = Readonly<{
  id: DataLayerTemplateId;
  data: DataLayerTemplateData;
}>;

export type DataLayerTemplatesListResponse = Readonly<{
  list: ReadonlyArray<DataLayerTemplate>;
}>;

export type CreateDataLayerTemplateRequest = Readonly<{
  data: DataLayerTemplateData;
}>;

export type CreateDataLayerTemplateResponse = Readonly<{
  template: DataLayerTemplate;
}>;

export const getDataLayerTemplates = (clientId: number): Promise<DataLayerTemplatesListResponse> => {
  const requestUrl = `/api/clients/${clientId}/data-layer-templates`;

  return apiGet(requestUrl)
    .then(response => ({
      list: response.list.map((template: Readonly<{ id: DataLayerTemplateId; data: DataLayerTemplateDataResponse }>) => {
        const { data } = template;

        // reusing the serializer function
        // as they both solve identical issues of converting server response to state
        // and are both stored on backend as a JSON
        const { id: _id, ...dataLayerTemplateFromServerModel } = mapSettingsDataLayersResponseInstanceToState({
          id: 'temporary',
          ...data,
        });

        return ({
          ...template,
          data: dataLayerTemplateFromServerModel,
        });
      }),
    }));
};

export const createDataLayerTemplate = (clientId: number, dataLayer: DataLayerTemplateData): Promise<CreateDataLayerTemplateResponse> => {
  const requestUrl = `/api/clients/${clientId}/data-layer-templates`;

  const request: CreateDataLayerTemplateRequest = {
    data: dataLayer,
  };

  return apiPost(requestUrl, request);
};

export type UpdateDataLayerTemplateRequest = Readonly<{
  data: DataLayerTemplateData;
}>;

export type UpdateDataLayerTemplateResponse = Readonly<{
  data: DataLayerTemplate;
}>;

export const updateDataLayerTemplate = (clientId: number, templateId: DataLayerTemplateId, data: DataLayerTemplateData): Promise<UpdateDataLayerTemplateResponse> => {
  const requestUrl = `/api/clients/${clientId}/data-layer-templates/${templateId}`;

  const request: UpdateDataLayerTemplateRequest = {
    data,
  };

  return apiPut(requestUrl, request);
};

export const deleteDataLayerTemplate = (clientId: number, templateId: DataLayerTemplateId): Promise<void> => {
  const requestUrl = `/api/clients/${clientId}/data-layer-templates/${templateId}`;

  return apiDelete(requestUrl);
};
